import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { publicApi, API } from "@/lib/api";
import { ChemText } from "@/components/ChemText";
import { Download, Home, CheckCircle2, XCircle, Trophy, Info } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

function getGradeLabel(percent) {
  if (percent >= 90) return "Świetnie!";
  if (percent >= 75) return "Bardzo dobrze";
  if (percent >= 50) return "Nieźle";
  if (percent >= 30) return "Trzeba poprawić";
  return "Powtórz materiał";
}

function getResultBg(percent) {
  if (percent >= 75) return "#D1FAE5";
  if (percent >= 50) return "#FEF08A";
  return "#FEE2E2";
}

export default function StudentResults() {
  const { code, studentId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    publicApi
      .get(`/public/sessions/${code}/result/${studentId}`)
      .then(({ data }) => { if (!cancelled) setData(data); });
    return () => { cancelled = true; };
  }, [code, studentId]);

  if (!data) return <div className="min-h-screen flex items-center justify-center">Ładowanie...</div>;

  const { session, submission } = data;
  const percent = submission.percent;
  const grade = getGradeLabel(percent);
  const bgColor = getResultBg(percent);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="border-b-2 border-[#0F172A] bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold" data-testid="back-home-link">
            <Home className="w-4 h-4" strokeWidth={2.5} />
            <span>Strona główna</span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#64748B]">Twój wynik</div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        {/* Result hero */}
        <div className="brut-lg rounded-md p-8 mb-6" style={{ backgroundColor: bgColor }}>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold">{session.title}</div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#64748B] mt-1">
                Kod: <span className="font-display font-black text-base text-[#0F172A]">{session.code}</span>
              </div>
              <h1 className="font-display font-black text-4xl mt-4" data-testid="result-name">
                {submission.name}
              </h1>
              <div className="font-display font-bold text-lg mt-1">{grade}</div>
            </div>
            <div className="text-right">
              <Trophy className="w-8 h-8 inline-block" strokeWidth={2.5} />
              <div className="font-display font-black text-7xl tracking-tight" data-testid="result-percent">
                {percent}%
              </div>
              <div className="font-bold">{submission.score}/{submission.total} punktów</div>
            </div>
          </div>

          <div className="flex gap-2 mt-6 flex-wrap">
            <a
              href={`${API}/public/sessions/${code}/student/${studentId}/download?format=pdf`}
              target="_blank"
              rel="noopener noreferrer"
              download={`${code}-${submission.name}.pdf`}
              className="brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md inline-flex items-center justify-center h-10 px-4 text-sm no-underline"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" strokeWidth={2.5} />Pobierz PDF
            </a>
            <a
              href={`${API}/public/sessions/${code}/student/${studentId}/download?format=csv`}
              target="_blank"
              rel="noopener noreferrer"
              download={`${code}-${submission.name}.csv`}
              className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md inline-flex items-center justify-center h-10 px-4 text-sm no-underline text-[#0F172A]"
              data-testid="download-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" strokeWidth={2.5} />Pobierz CSV
            </a>
          </div>
        </div>

        {/* Auto-submit notice — helps students understand why some answers
            are blank (they ran out of time / teacher ended the quiz early). */}
        {submission.auto_submitted && (
          <div
            className="brut bg-[#FEF3C7] rounded-md p-4 mb-6 flex items-start gap-3"
            data-testid="auto-submit-notice"
          >
            <Info className="w-5 h-5 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
            <div className="text-sm">
              <div className="font-black">Arkusz został oddany automatycznie</div>
              <div className="text-[#334155] mt-0.5">
                Nauczyciel zakończył kartkówkę lub upłynął czas — system sam wysłał
                Twoje ostatnio zapisane odpowiedzi. Nieodpowiedziane pytania są
                oznaczone jako „(brak)" i policzone jako błędne.
              </div>
            </div>
          </div>
        )}

        {/* Detailed answers */}
        <h2 className="font-display font-black text-2xl mb-4">Twoje odpowiedzi</h2>
        <div className="space-y-3">
          {submission.answers.map((a, i) => (
            <div key={i} className={`brut rounded-md p-5 ${a.is_correct ? "bg-[#D1FAE5]" : "bg-[#FEE2E2]"}`} data-testid={`answer-${i}`}>
              <div className="flex items-start gap-3">
                {a.is_correct ? (
                  <CheckCircle2 className="w-6 h-6 text-[#10B981] mt-1 flex-shrink-0" strokeWidth={2.5} />
                ) : (
                  <XCircle className="w-6 h-6 text-[#EF4444] mt-1 flex-shrink-0" strokeWidth={2.5} />
                )}
                <div className="flex-1">
                  <div className="font-bold">{i + 1}. <ChemText text={a.question_text} /></div>
                  {a.image_url && (
                    <div className="mt-2">
                      <img
                        src={a.image_url}
                        alt="Ilustracja do pytania"
                        className="brut rounded-md max-w-full max-h-56 object-contain bg-white"
                        loading="lazy"
                      />
                    </div>
                  )}
                  {a.type === "mc" && a.options && (
                    <div className="text-sm text-[#334155] mt-2 grid gap-1">
                      {a.options.map((opt, idx) => {
                        const letter = String.fromCharCode(65 + idx);
                        const isCorrect = a.correct_answer === letter;
                        const isGiven = a.given === letter;
                        return (
                          <div key={idx} className={`${isCorrect ? "font-bold text-[#10B981]" : ""} ${isGiven && !isCorrect ? "line-through" : ""}`}>
                            {letter}) <ChemText text={opt} />
                            {isCorrect && " ✓"}
                            {isGiven && !isCorrect && " ← Twoja"}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="mt-2 text-sm">
                    <div>Twoja odpowiedź: <b><ChemText text={a.given || "(brak)"} /></b></div>
                    <div>Poprawna odpowiedź: <b><ChemText text={a.correct_answer} /></b></div>
                  </div>
                  {a.explanation && (
                    <div
                      className="brut bg-white rounded-md p-3 mt-3 text-sm flex items-start gap-2"
                      data-testid={`explanation-${i}`}
                    >
                      <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#0F172A]" strokeWidth={2.5} />
                      <div>
                        <div className="text-xs tracking-[0.2em] uppercase font-bold mb-1">Wyjaśnienie</div>
                        <div className="text-[#334155]"><ChemText text={a.explanation} /></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
